import { useEffect } from "react";
import moment from "moment";
import { capitalFirstLetter, findTypeLabel } from "../../../utils";
import PrivacyPolicyCheckbox from "../../PrivacyPolicyCheckbox";
import { getPolicyHtml } from "../../../utils/policies";
import { useBookingContext } from "../../../context/BookingContext";

const Step4 = ({ formik, data, priceCalculate, t, currentLanguage, onSubmit, disabled, bookingData }) => {
  // const { dateUsage } = useBookingContext();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    return () => {
      formik.setFieldValue("privacyPolicy", false);
    };
  }, []);

  return (
    <div className="px-4 pt-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 bg-blue-100 rounded-full">
            <i className="text-4xl text-blue-500 fa-solid fa-file-invoice"></i>
          </div>
          <h3 className="mb-2 text-2xl font-bold text-gray-800">{t("order_summary") || "Order Summary"}</h3>
          <p className="text-gray-600">
            {t("please_review_order") || "Please review your order details before proceeding to payment"}
          </p>
        </div>

        {/* Customer Information */}
        <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h4 className="pb-3 mb-4 text-lg font-semibold text-gray-800 border-b">
            {t("customer_information") || "Customer Information"}
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-gray-600">{t("name") || "Name"}</p>
              <p className="font-semibold text-gray-800">{formik.values.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t("email") || "Email"}</p>
              <p className="font-semibold text-gray-800">{formik.values.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t("phone") || "Phone"}</p>
              <p className="font-semibold text-gray-800">+{formik.values.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t("address") || "Address"}</p>
              <p className="font-semibold text-gray-800">{formik.values.address}</p>
            </div>
          </div>
        </div>

        {/* Booking Details */}
        <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h4 className="pb-3 mb-4 text-lg font-semibold text-gray-800 border-b">
            {t("booking_details") || "Booking Details"}
          </h4>
          <div className="space-y-4">
            <div className="flex items-start">
              <i className="mt-1 mr-3 text-gray-500 fa-solid fa-car"></i>
              <div>
                <p className="text-sm text-gray-600">{t("vehicle") || "Vehicle"}</p>
                <p className="font-semibold text-gray-800">
                  {capitalFirstLetter(data?.car?.brands)} {capitalFirstLetter(data?.car?.name)}
                </p>
                <p className="text-sm text-gray-600">
                  {data?.conditions?.with_driver ? t("with_driver") : t("without_driver")} - {findTypeLabel(data?.type)}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <i className="mt-1 mr-3 text-gray-500 fa-solid fa-calendar-days"></i>
              <div className="flex-1">
                <p className="text-sm text-gray-600">{t("rental_period") || "Rental Period"}</p>
                <div className="grid grid-cols-1 gap-2 mt-2 md:grid-cols-2">
                  {data.type === "tour" ? (
                    <>
                      <div>
                        <p className="text-xs text-gray-500">{t("tourPackage")}</p>
                        <p className="font-semibold text-gray-800">{data?.tourPackage}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{t("duration")}</p>
                        <p className="font-semibold text-gray-800">{data?.duration}</p>
                      </div>
                    </>
                  ) : null}
                  <div>
                    <p className="text-xs text-gray-500">{t("pickup_date_n_time")}</p>
                    <p className="font-semibold text-gray-800">
                      {moment(data?.pickup_date).format("DD/MM/YYYY [-] HH:mm")}
                    </p>
                  </div>
                  {data?.type !== "transfer_service" && (
                    <div>
                      <p className="text-xs text-gray-500">{t("dropoff_date_n_time")}</p>
                      <p className="font-semibold text-gray-800">
                        {(() => {
                          if (data?.type === "tour") {
                            const pickup = moment(data?.pickup_date, "YYYY/MM/DD HH:mm");
                            const daysToAdd = Number(data?.duration) - 1;
                            const hoursToAdd = Number(data?.tourPackage);

                            const dropoff = pickup.clone().add(daysToAdd, "days").add(hoursToAdd, "hours");

                            return dropoff.format("DD/MM/YYYY [-] HH:mm");
                          }

                          return moment(data?.dropoff_date).format("DD/MM/YYYY [-] HH:mm");
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <i className="mt-1 mr-3 text-gray-500 fa-solid fa-location-dot"></i>
              <div className="flex-1">
                <p className="text-sm text-gray-600">{t("locations") || "Locations"}</p>
                <div className="grid grid-cols-1 gap-2 mt-2 md:grid-cols-2">
                  <div>
                    <p className="text-xs text-gray-500">{t("pickup_location")}</p>
                    <p className="font-semibold text-gray-800">{data?.pickup_location}</p>
                  </div>
                  {data.type === "self_drive" ? (
                    <div>
                      <p className="text-xs text-gray-500">{t("dropoff_location")}</p>
                      <p className="font-semibold text-gray-800">{data?.dropoff_location}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {formik.values.notes && (
              <div className="flex items-start">
                <i className="mt-1 mr-3 text-gray-500 fa-solid fa-note-sticky"></i>
                <div>
                  <p className="text-sm text-gray-600">{t("notes") || "Notes"}</p>
                  <p className="text-gray-800">{formik.values.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* {(dateUsage?.inTown?.length > 0 || dateUsage?.outOfTown?.length > 0) && (
          <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
            <h4 className="pb-3 mb-4 text-lg font-semibold text-gray-800 border-b">
              {t("location_usage") || "Usage Location Details"}
            </h4>

            {dateUsage?.inTown?.length > 0 && (
              <div className="mb-4">
                <p className="mb-2 font-semibold text-green-600">{t("in_town_usage") || "In Town Usage"}</p>
                {dateUsage.inTown.map((item, index) => (
                  <div key={index} className="flex justify-between py-2 text-sm text-gray-700 border-b">
                    <span>{moment(item.date).format("DD/MM/YYYY")}</span>
                    <span>{`${t("zone")} ${item.zone.join(", ")}`}</span>
                  </div>
                ))}
              </div>
            )}

            {dateUsage?.outOfTown?.length > 0 && (
              <div>
                <p className="mb-2 font-semibold text-red-600">{t("out_of_town_usage") || "Out of Town Usage"}</p>
                {dateUsage.outOfTown.map((item, index) => (
                  <div key={index} className="flex justify-between py-2 text-sm text-gray-700 border-b">
                    <span>{moment(item.date).format("DD/MM/YYYY")}</span>
                    <span>{`${t("zone")} ${item.zone.join(", ")}`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )} */}

        {/* Price Breakdown */}
        <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h4 className="pb-3 mb-4 text-lg font-semibold text-gray-800 border-b">
            {t("price_breakdown") || "Price Breakdown"}
          </h4>

          <div className="space-y-3">
            {priceCalculate?.prices?.map((item, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="text-gray-600">{item.date}</span>
                <span className="font-semibold text-gray-800">Rp {item.price?.toLocaleString("id-ID")}</span>
              </div>
            ))}

            <div className="pt-3 mt-4 border-t-2 border-gray-300">
              <div className="flex justify-between">
                <span className="text-xl font-bold text-gray-800">{t("total_amount") || "Total Amount"}</span>
                <span className="text-2xl font-bold text-[#f5272b]">
                  Rp {priceCalculate?.total?.toLocaleString("id-ID")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Policy Checkbox - DIPINDAHKAN KE SINI */}
        <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h4 className="pb-3 mb-4 text-lg font-semibold text-gray-800 border-b">
            {t("review_n_confirm") || "Review & Confirm"}
          </h4>
          <PrivacyPolicyCheckbox
            checked={formik.values.privacyPolicy}
            onChange={(val) => formik.setFieldValue("privacyPolicy", val)}
            error={formik.touched.privacyPolicy && Boolean(formik.errors.privacyPolicy)}
            helperText={formik.errors.privacyPolicy}
            policyHtml={getPolicyHtml(data?.type, currentLanguage)}
          />
        </div>

        {/* Important Notes */}
        {/* <div className="p-4 rounded-lg bg-yellow-50">
          <div className="flex items-start">
            <i className="mt-1 mr-3 text-yellow-600 fa-solid fa-exclamation-triangle"></i>
            <div className="text-sm text-gray-700">
              <p className="mb-2 font-semibold">{t("important_notes") || "Important Notes"}:</p>
              <ul className="pl-5 space-y-1 list-disc">
                <li>{t("note_1") || "Please ensure all information provided is accurate"}</li>
                <li>{t("note_2") || "You will be redirected to payment page in the next step"}</li>
                <li>{t("note_3") || "Booking confirmation will be sent to your email after payment"}</li>
                <li>{t("note_4") || "Please keep your booking reference for future correspondence"}</li>
              </ul>
            </div>
          </div>
        </div> */}

        {/* Submit Button - DIPINDAHKAN KE SINI */}
        <div className="mt-6">
          <button
            type="button"
            onClick={onSubmit}
            disabled={disabled}
            className={`w-full font-bold py-6 px-6 rounded transition-colors ${
              disabled ? "bg-gray-400 cursor-not-allowed text-gray-600" : "bg-[#f5272b] hover:bg-red-600 text-white"
            }`}
          >
            {disabled ? (
              <span className="flex items-center justify-center">
                <i className="mr-2 fa-solid fa-spinner fa-spin"></i>
                {t("loading") || "Processing..."}
              </span>
            ) : bookingData ? (
              <span className="flex items-center justify-center">
                <i className="mr-2 fa-solid fa-money-bill"></i>
                Payment
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <i className="mr-2 fa-solid fa-check-circle"></i>
                {t("reserve_now") || "Reserve Now"}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step4;
